import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { subscribeEmail, SubscriptionData } from '../api/subscription';

// Constants for localStorage keys
export const EMAIL_SUBSCRIPTION_KEY = 'mydatavalue_email_subscribed';

/**
 * Hook for managing email subscription state based on URL parameters and localStorage
 */
export const useEmailSubscription = () => {
  // Use React Router hooks to access URL search parameters
  const [searchParams] = useSearchParams();

  // Check if the isSubscribed parameter is in the URL
  const isSubscribedInUrl = searchParams.get('isSubscribed') === 'true';

  // Check if we have an email in localStorage (meaning user is subscribed)
  const hasSubscribedInStorage = localStorage.getItem(EMAIL_SUBSCRIPTION_KEY) === 'true';

  // Effect to store email in localStorage when isSubscribed parameter is detected
  useEffect(() => {
    if (isSubscribedInUrl) {
      localStorage.setItem(EMAIL_SUBSCRIPTION_KEY, 'true');
    }
  }, [isSubscribedInUrl]);

  // Create mutation for subscription
  const subscriptionMutation = useMutation({
    mutationFn: (data: SubscriptionData) => {
      // Get the current URL to use as the return URL
      const currentPath = window.location.pathname;

      // Create a return URL that will include the isSubscribed parameter
      const returnUrl = `${window.location.origin}${currentPath}?isSubscribed=true`;

      return subscribeEmail(data, returnUrl);
    }
  });

  // Determine if user has subscribed based on localStorage
  const hasSubscribed = hasSubscribedInStorage || isSubscribedInUrl;

  // Show modal only if not subscribed
  const shouldShowSubscriptionModal = !hasSubscribed;

  return {
    hasSubscribed,
    shouldShowSubscriptionModal,
    subscribeEmail: subscriptionMutation.mutateAsync,
    isSubscribing: subscriptionMutation.isPending,
    error: subscriptionMutation.error
  };
};

export default useEmailSubscription;